import { useEffect, useState } from "react";
import { useGetTargetList } from "../../../../helpers/queries/targets/useGetTargets";
import AddTargetModal from "../modals/AddTargetModal";
import { Spinner } from "../../../../components/Spinner";
import RunScanModal from "../modals/RunScanModal";
import { FiCompass, FiEdit, FiGlobe, FiTrash2 } from "react-icons/fi";
import { Button } from "../../../../components/ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteTarget } from "../../../../helpers/api";
import DeleteModal from "../../../../components/modals/DeleteModal";
import { toast } from "react-toastify";
import empty from "../../../../assets/images/empty_assets.png";
import { usePaginationSync } from "../../../../hooks/usePaginationSync";
import { PaginationComponent } from "../../../../components/Pagination";
import EditTargetModal from "../modals/EditTargetModal";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useGetUserRole } from "../../../../hooks/useGetUserRole";
import UploadInventoryModal from "../../Inventory/modal/UploadInventory";
import { AiOutlineFileAdd } from "react-icons/ai";
import { cn } from "../../../../lib/utils";
import {
  TooltipProvider,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "../../../../components/ui/tooltip";
const TargetList = () => {
  const { id } = useParams();
  const [lastPage, setLastPage] = useState(1);
  const { currentPage, updatePage } = usePaginationSync(lastPage);
  const { isLoading, isSuccess, isError, data } = useGetTargetList(
    id,
    currentPage
  );
  // const assetList = true;
  const parentTarget = data?.data?.name || "";
  const parentAssetType = data?.data?.asset_type || "";
  const list = data?.data?.target?.data;
  const location = useLocation();
  const { inventory } = location.state || false;
  const { targetData } = location.state || {};
  const [openEdit, setOpenEdit] = useState(false);
  const [openScan, setOpenScan] = useState(false);

  const { role, viewer } = useGetUserRole();

  useEffect(() => {
    setLastPage(data?.data?.target?.pagination?.total_pages);
  }, [data]);

  const navigate = useNavigate();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const handleDeleteModal = () => setOpenDeleteModal(false);

  const queryClient = useQueryClient();
  const { mutate: deleteMutation, isPending } = useMutation({
    mutationFn: (id) => deleteTarget(id),
    onSuccess: () => {
      toast("Asset deleted successfully");
      handleDeleteModal();
      queryClient.invalidateQueries({ queryKey: ["targets"] });
      navigate("/dashboard/assets");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete asset");
    },
  });

  const handleDelete = (id) => deleteMutation(id);

  return (
    <>
      <div className="w-full">
        <div className=" w-full lg:block flex justify-between items-center py-6 lg:px-10 md:px-5 px-4 bg-white border-b mb-4">
          {inventory ? (
            <div className="w-full flex lg:flex-row flex-col justify-between lg:items-center gap-4 mb-1">
              <div className="flex gap-3 items-center">
                <div>
                  <div className="w-[50px] h-[50px] rounded-full border border-neutral300 flex justify-center items-center">
                    <AiOutlineFileAdd className="text-neutral700 text-[20px]" />
                  </div>
                </div>
                <div>
                  <p className="text-darkGrey font-semibold md:block hidden">
                    Inventory
                  </p>
                  <p className="text-neutral600 md:text-base text-sm">
                    Manage your assets - domains and IP addresses
                  </p>
                </div>
              </div>

              {!viewer && <UploadInventoryModal />}
            </div>
          ) : (
            <div className="w-full flex lg:flex-row flex-col justify-between lg:items-center gap-4 mb-1">
              <div className="flex gap-3 items-center">
                <div>
                  <div className="w-[50px] h-[50px] rounded-full border border-neutral300 flex justify-center items-center">
                    <FiGlobe className="text-neutral700 text-[20px]" />
                  </div>
                </div>
                <div>
                  <p className="text-darkGrey font-semibold md:block hidden">
                    Targets
                  </p>
                  <p className="text-neutral600 md:text-base text-sm">
                    Add and monitor the websites or IP addresses you want to
                    keep secure.
                  </p>
                </div>
              </div>
              {role && role !== "Viewer" && <AddTargetModal />}
            </div>
          )}
        </div>

        {isLoading && !isSuccess && (
          <div className="h-[55vh] w-full flex items-center justify-center">
            <Spinner />
          </div>
        )}

        {isError && !isLoading && (
          <div className="h-[45vh] w-full flex justify-center flex-col items-center">
            <p className="font-semibold text-xl text-center">
              There was an error getting the data
            </p>
            <p className="text-center">{data?.message ?? ""}</p>
          </div>
        )}

        {!isLoading && isSuccess && list?.length > 0 && (
          <>
            <div className="lg:px-10 md:px-5 px-4 flex justify-between items-center">
              <p>
                Showing results for{" "}
                <span className="font-medium"> {parentTarget}</span>
              </p>
              {role && role !== "Viewer" && (
                <div className="flex items-center gap-4 mr-3">
                  <Button
                    className="w-fit bg-neutral200 hover:bg-neutral300 text-neutral1000"
                    variant={"ghost"}
                    onClick={() => {
                      setOpenScan(true);
                    }}
                  >
                    <FiCompass size={20} />
                    <span className="font-medium">Scan</span>
                  </Button>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          className="w-fit bg-neutral200 hover:bg-neutral300 text-neutral1000"
                          variant="ghost"
                          onClick={() => setOpenEdit(true)}
                        >
                          <FiEdit size={20} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Edit target</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                        <TooltipTrigger asChild>

                          <DeleteModal
                      onOpenChange={() => setOpenDeleteModal(!openDeleteModal)}
                      open={openDeleteModal}
                      data={data?.data?.name}
                      id={data?.data?.id}
                      title="Delete asset"
                      handleDelete={handleDelete}
                      loading={isPending}
                    >
                      
                          <Button
                            className="w-fit bg-[#FF44441A] hover:bg-[#ffdada] text-[#E12A5B]"
                            variant="ghost"
                          >
                            <FiTrash2 size={20} />
                          </Button>
                    </DeleteModal>
                    
                        </TooltipTrigger>
                        <TooltipContent>Delete target</TooltipContent>
                      </Tooltip>

                    
                  </TooltipProvider>
                </div>
              )}
            </div>

            <div className="lg:p-10 md:p-5 p-4 bg-white h-[100%]">
              <div className="overflow-x-auto">
                <div className="w-full border min-w-[1100px] border-neutral300 rounded-lg text-sm">
                  <div className="flex justify-between text-left px-3 xl:px-6 py-2 bg-neutral3 rounded-t-lg border-b text-neutral700 font-medium w-full">
                    <span className="flex-1 mr-2">Target</span>

                    {role && role !== "Viewer" && (
                      <span className="w-[15%]">Action</span>
                    )}
                  </div>

                  {list?.map((item, index) => {
                    return (
                      <section
                        key={index}
                        className={`min-h-[60px] py-2 px-3 xl:px-6 flex text-neutral900  items-center ${
                          index === 0
                            ? "border-t-0"
                            : "border-t border-t-[#EAECF0]"
                        } hover:bg-[#F9FAFB]`}
                      >
                        <div
                          className={cn(
                            inventory && "uppercase",
                            "flex-1 mr-4 text-left break-all truncate overflow-hidden text-nowrap"
                          )}
                        >
                          {item}
                        </div>

                        {role && role !== "Viewer" && (
                          <div className="w-[15%] flex items-center space-x-3">
                            <AddTargetModal
                              triggerLabel="Scan"
                              modalTitle="Create and Scan Asset"
                              targetData={{
                                name: parentTarget,
                                asset_type: parentAssetType,
                                target: item,
                              }}
                              icon={<FiCompass size={20} />}
                            />
                          </div>
                        )}
                      </section>
                    );
                  })}
                </div>
              </div>

              <PaginationComponent
                lastPage={data?.data?.target?.pagination?.total_pages}
                currentPage={currentPage}
                handlePageChange={updatePage}
              />
            </div>
          </>
        )}

        {list?.length === 0 && !isLoading && isSuccess && (
          <div className="h-[45vh] mt-[6rem] w-full flex justify-center flex-col items-center text-center p-4">
            <img src={empty} alt="empty" />
            <p className=" font-semibold my-2 text-darkGrey">
              No assets added yet
            </p>
            <p className="text-neutral600 text-sm max-w-[360px] mb-4">
              Start by adding your first asset. This can be domains or IP
              addresses you'd like to monitor for vulnerabilities.
            </p>
            {role && role !== "Viewer" && <AddTargetModal />}
          </div>
        )}
      </div>

      <EditTargetModal
        data={targetData}
        openEdit={openEdit}
        setOpenEdit={setOpenEdit}
        assetList={true}
      />

      <RunScanModal
        data={targetData}
        openScan={openScan}
        setOpenScan={setOpenScan}
        // assetList={true}
      />
    </>
  );
};

export default TargetList;
